import React, { createContext, useContext, useEffect, useState } from 'react';
import * as R from 'ramda';

import eventHandler, { EventName } from '../services/eventHandler';
import { userAnalytics } from '../services/analytics';
import authenticationService, { UserFeedbackOptions, UserFeedbackRequest } from '../services/authentication';
import { Metadata } from '../services/metadata';
import Log from '../common/logging';
import { TechnicalError } from '../common/errors';
import { getStoredMetadata, storeMetadata } from '../utils/storage';
import stringUtils from '../common/stringUtils';
import { MemoryRouterContext } from './MemoryRouterContext';

export interface MetadataProps {
  metadata: Metadata;
  truncatedDid(): string;
  truncatedValidationId(): string;
  submitUserFeedback(feedback: UserFeedbackOptions): Promise<void>;
}

export const MetadataContext = createContext<MetadataProps>({} as MetadataProps);

type MetadataProviderProps = {
  children: JSX.Element;
};

/**
 * Provider that adds the metadata context
 * provides functionality to handle the collection of user metadata for inclusion in other services
 *
 * @param props
 * @constructor
 */
export function MetadataProvider(props: MetadataProviderProps): JSX.Element {
  const { children } = props;
  const [metadata, setMetadata] = useState<Metadata>({});
  const [eventMetadata, setEventMetadata] = useState<Metadata>({});
  const { queryParams } = useContext(MemoryRouterContext);
  const { action } = queryParams || {};

  useEffect(() => {
    eventHandler.addEventListener<Metadata>(EventName.addMetadata, setEventMetadata);
  }, []);

  /**
   * Update the metadata from the incoming event metadata data. This is done using
   * a separate state variable as merging the new data with the existing data directly
   * in the event listener doesn't work as the event listener has stale data.
   */
  useEffect(() => {
    // The first time network address is set we trigger the reseed of data
    // from storage so screens that are opened independently still have access.
    if (!R.isNil(eventMetadata.networkAddress)) {
      // Read the stored data
      const storedMetadata = getStoredMetadata(eventMetadata.networkAddress);
      // Then merge in the current metadata
      let mergedMetadata = R.mergeDeepRight(metadata, storedMetadata);
      // Then merge in metadata from originating event
      mergedMetadata = R.mergeDeepRight(mergedMetadata, eventMetadata);
      setMetadata(mergedMetadata);
    } else {
      setMetadata(R.mergeDeepRight(metadata, eventMetadata));
    }
  }, [eventMetadata]);

  useEffect(() => {
    // We don't have a GKN during the link flow, so we don't want to store metadata
    if (R.isEmpty(metadata) || action === 'link') {
      return;
    }
    storeMetadata(metadata);
    userAnalytics.updateFromMetaData(metadata);
    Log.updateFromMetaData(metadata);
    Log.debug(`Metadata updated: ${JSON.stringify(metadata, null, 2)}`);
  }, [metadata]);

  /**
   * Return the did, truncated to the first 6 characters following the scheme and method
   */
  function truncatedDid(): string {
    const { did } = metadata;
    return stringUtils.truncateDid(did || '');
  }

  /** Return the validation Id truncated to the first 6 characters */
  function truncatedValidationId(): string {
    return metadata.validationId?.substring(0, 6) || '';
  }

  /**
   * Submit the user data, enriched with all currently set metadata
   */
  async function submitUserFeedback(feedback: UserFeedbackOptions): Promise<void> {
    const { networkAddress } = metadata;
    if (!networkAddress) {
      Log.error(
        'Submit user feedback failed',
        new TechnicalError('Unable to submit user feedback, network address not set'),
      );
      return;
    }

    const feedbackRequest: UserFeedbackRequest = {
      feedback,
      networkAddress,
      did: metadata.did,
      flowId: metadata.flowId,
      validationId: metadata.validationId,
      wallet: metadata.wallet,
      domain: metadata.domain,
    };

    const feedbackResult = await authenticationService.submitUserFeedback(feedbackRequest);
    if (feedbackResult.isFailed()) {
      Log.error(
        'Submit user feedback failed',
        new TechnicalError('Unable to submit user feedback, API error', feedbackResult.failure()),
      );
    }
  }

  return (
    <MetadataContext.Provider
      value={{
        metadata,
        truncatedValidationId,
        truncatedDid,
        submitUserFeedback,
      }}
    >
      {children}
    </MetadataContext.Provider>
  );
}

export const useMetadata = (): MetadataProps => useContext(MetadataContext);
